import { Alert, Box } from "@mui/material"
import type { FC } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import type { GlTransactionFormValues } from "../schemas/glTransactionForm"
import { getAccountingPeriod } from "../utils/glTransactionFormUtils"

import { AerosTextField } from "@/components/UiKit"
import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"

interface GlTransactionDateSectionProps {
  disabled?: boolean
  showRecurringLabel?: boolean
  showAlerts?: boolean
  testIdPrefix: string
}

export const GlTransactionDateSection: FC<GlTransactionDateSectionProps> = ({
  disabled,
  showRecurringLabel,
  showAlerts = true,
  testIdPrefix,
}) => {
  const { control } = useFormContext<GlTransactionFormValues>()
  const transactionDate = useWatch({ control, name: "transactionDate" })
  const isRecurring = useWatch({ control, name: "isRecurring" })

  const accountingPeriod = transactionDate ? getAccountingPeriod(transactionDate) : null
  const dateLabel = showRecurringLabel && isRecurring ? "Transaction Start Date" : "Transaction Date"

  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
  const isFutureDate = !!transactionDate && transactionDate > todayStr

  return (
    <>
      <Box className='flex flex-col gap-1'>
        <FormDatePicker
          control={control}
          name='transactionDate'
          label={dateLabel}
          required
          disabled={disabled}
          data-testid={`${testIdPrefix}.transaction-date`}
        />
        {showAlerts && accountingPeriod && (
          <Box className='flex flex-col gap-1 px-1'>
            {accountingPeriod.type === "past" && (
              <Alert severity='warning' className='py-1 text-xs'>
                You&apos;ve entered a transaction date that falls in a previous accounting period. Please double-check
                before clicking Save.
              </Alert>
            )}
            {isFutureDate && (
              <Alert severity='info' className='py-1 text-xs'>
                This transaction will be scheduled and posted automatically on {transactionDate}.
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <AerosTextField
        label='Accounting Period'
        value={accountingPeriod?.label ?? ""}
        disabled
        size='small'
        helperText={accountingPeriod?.range ?? " "}
        data-testid={`${testIdPrefix}.accounting-period`}
      />
    </>
  )
}
